import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { auctionService, reviewService } from '../services/api';

interface AuctionCard {
  id: number;
  title?: string;
  status: string;
  startPrice: number;
  currentPrice: number;
  minIncrement: number;
  startTime: string;
  endTime: string;
  imageUrls?: string;
  batchId?: string;
  card?: {
    id: number;
    name: string;
    imageUrl?: string;
    rarity?: string;
    type?: string;
  };
  // New items structure
  items?: {
    card: {
      id: number;
      name: string;
      imageUrl?: string;
      rarity?: string;
    }
  }[];
  seller?: {
    id: number;
    displayName: string;
    profilePicture?: string;
  };
}

const statusStyles: Record<string, string> = {
  LIVE: 'bg-green-600 text-white',
  SCHEDULED: 'bg-yellow-500 text-white',
  ENDED: 'bg-gray-600 text-white',
  CANCELLED: 'bg-red-600 text-white',
};

const AuctionListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [auctions, setAuctions] = useState<AuctionCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'LIVE' | 'SCHEDULED' | 'ENDED' | 'ALL'>('LIVE');
  const [sortBy, setSortBy] = useState('endTime');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [userRatings, setUserRatings] = useState<Record<number, { averageRating: number; totalReviews: number }>>({});

  const isAuthenticated = Boolean(localStorage.getItem('token'));

  useEffect(() => {
    fetchAuctions();
  }, [statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    const sellerIds = Array.from(new Set(auctions.map((a) => a.seller?.id).filter(Boolean))) as number[];
    const idsToFetch = sellerIds.filter((id) => userRatings[id] === undefined);
    if (idsToFetch.length === 0) return;

    const fetchRatings = async () => {
      const entries = await Promise.all(
        idsToFetch.map(async (id) => {
          try {
            const res = await reviewService.getUserReviews(String(id), { page: 1, limit: 1 });
            const summary = res.data.data.summary;
            return [id, { averageRating: summary?.averageRating || 0, totalReviews: summary?.totalReviews || 0 }] as const;
          } catch (err) {
            console.error('Failed to fetch rating for user', id, err);
            return [id, { averageRating: 0, totalReviews: 0 }] as const;
          }
        })
      );
      setUserRatings((prev) => {
        const next = { ...prev };
        entries.forEach(([id, rating]) => {
          next[id] = rating;
        });
        return next;
      });
    };

    fetchRatings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auctions]);

  const fetchAuctions = async () => {
    try {
      setLoading(true);
      const params: any = { sortBy, sortOrder };
      if (search) params.search = search;
      if (statusFilter !== 'ALL') params.status = statusFilter;
      const response = await auctionService.getAuctions(params);
      setAuctions(response.data.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load auctions', err);
      setError('Failed to load auctions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getPrimaryImage = (auction: AuctionCard) => {
    if (auction.imageUrls) {
      try {
        const parsed = JSON.parse(auction.imageUrls);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed[0];
        }
      } catch (_e) {
        // ignore
      }
    }
    if (auction.items && auction.items.length > 0) {
      return auction.items[0].card.imageUrl;
    }
    return auction.card?.imageUrl;
  };

  const getCountdown = (endTime: string) => {
    try {
      return formatDistanceToNow(new Date(endTime), { addSuffix: true });
    } catch {
      return '';
    }
  };

  const renderStars = (rating: number) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className={star <= rating ? 'text-amber-500' : 'text-gray-300'}>
          ★
        </span>
      ))}
    </div>
  );

  const getDisplayName = (auction: AuctionCard) => {
    if (auction.title) return auction.title;
    if (auction.items && auction.items.length > 0) {
      if (auction.items.length > 1) {
        return `${auction.items[0].card.name} + ${auction.items.length - 1} more`;
      }
      return auction.items[0].card.name;
    }
    return auction.card?.name || 'Auction';
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('home.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-start gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-bold mb-2">{t('auctions.title')}</h1>
          <p className="text-gray-600">{t('auctions.subtitle')}</p>
        </div>
        {isAuthenticated && (
          <button
            onClick={() => navigate('/auctions/new')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-medium"
          >
            + {t('auctions.createAuction')}
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            fetchAuctions();
          }}
          className="grid md:grid-cols-5 gap-3"
        >
          <input
            type="text"
            placeholder={t('auctions.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent md:col-span-2"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="LIVE">{t('auctions.status.live')}</option>
            <option value="SCHEDULED">{t('auctions.status.scheduled')}</option>
            <option value="ENDED">{t('auctions.status.ended')}</option>
            <option value="ALL">{t('auctions.status.all')}</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="endTime">{t('auctions.sort.endingSoon')}</option>
            <option value="startPrice">{t('auctions.sort.startPrice')}</option>
            <option value="currentPrice">{t('auctions.sort.currentPrice')}</option>
          </select>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as any)}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="ASC">{t('marketplace.ascending')}</option>
            <option value="DESC">{t('marketplace.descending')}</option>
          </select>
          <button
            type="submit"
            className="md:col-span-5 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-medium"
          >
            {t('auctions.apply')}
          </button>
        </form>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 justify-items-center">
        {auctions.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <div className="text-6xl mb-4">🤝</div>
            <p className="text-gray-600 text-lg">{t('auctions.noAuctions')}</p>
            <p className="text-gray-400 mt-2">{t('auctions.checkBack')}</p>
          </div>
        ) : (
          auctions.map((auction) => {
            const image = getPrimaryImage(auction);
            const countdown = auction.status === 'LIVE' ? getCountdown(auction.endTime) : '';
            const rarity = auction.card?.rarity || (auction.items?.[0]?.card.rarity);

            return (
              <div key={auction.id} className="group max-w-[250px] w-full">
                <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden hover:border-blue-500 hover:shadow-xl transition-all duration-200">
                  <Link to={auction.batchId ? `/auctions/batch/${auction.batchId}` : `/auctions/${auction.id}`} className="block aspect-[2.5/3.5] bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
                    {image ? (
                      <img
                        src={image}
                        alt={getDisplayName(auction)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-4xl text-gray-400">
                        🎴
                      </div>
                    )}
                    <div className="absolute top-2 left-2">
                      <span
                        className={`px-2 py-1 text-xs font-bold rounded shadow ${statusStyles[auction.status] || 'bg-gray-700 text-white'}`}
                      >
                        {auction.status === 'LIVE' ? t('auctions.status.live') :
                          auction.status === 'SCHEDULED' ? t('auctions.status.scheduled') :
                            auction.status === 'ENDED' ? t('auctions.status.ended') : auction.status}
                      </span>
                    </div>
                    {rarity && (
                      <div className="absolute top-2 right-2 px-2 py-1 text-xs font-semibold rounded bg-white/80 text-gray-800">
                        {rarity}
                      </div>
                    )}
                    {auction.items && auction.items.length > 1 && (
                      <div className="absolute bottom-2 right-2 px-2 py-1 text-xs font-bold rounded bg-blue-600 text-white shadow">
                        +{auction.items.length - 1} more
                      </div>
                    )}
                  </Link>
                  <div className="p-4">
                    <Link to={auction.batchId ? `/auctions/batch/${auction.batchId}` : `/auctions/${auction.id}`}>
                      <h3 className="font-bold text-lg mb-1 truncate group-hover:text-blue-600 transition">
                        {getDisplayName(auction)}
                      </h3>
                    </Link>
                    <p className="text-sm text-gray-600">
                      {t('auctions.currentBid')}
                      <span className="font-semibold text-blue-700 ml-1">
                        {Number(auction.currentPrice || auction.startPrice).toLocaleString('en-US', { maximumFractionDigits: 0 })} THB
                      </span>
                    </p>
                    <p className="text-xs text-gray-500 mb-2">
                      {t('auctions.minIncrement')} {Number(auction.minIncrement || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} THB
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-500 gap-2">
                      <div className="min-w-0">
                        <span>{t('auctions.seller')} </span>
                        {auction.seller?.id ? (
                          <Link to={`/users/${auction.seller.id}/reviews`} className="text-blue-700 font-semibold hover:underline">
                            {auction.seller.displayName}
                          </Link>
                        ) : (
                          <span>{auction.seller?.displayName || 'Unknown'}</span>
                        )}
                        {auction.seller?.id && userRatings[auction.seller.id] && (
                          <div className="mt-1 flex items-center gap-1 text-xs text-gray-600">
                            {renderStars(Math.round(userRatings[auction.seller.id].averageRating))}
                            <span className="font-semibold text-gray-800">
                              {userRatings[auction.seller.id].averageRating.toFixed(1)}
                            </span>
                            <span className="text-gray-500">({userRatings[auction.seller.id].totalReviews})</span>
                          </div>
                        )}
                      </div>
                      {countdown && <span className="text-red-600 font-medium whitespace-nowrap">{countdown}</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AuctionListPage;
